import html2canvas from "html2canvas";

const COUPON_SURFACE_SELECTORS = [
    "#betslip-capture",
    ".coupon-wrapper",
    ".betslip-mobile-coupon-wrapper",
];

// Gönderim görüntü yüzünden beklemesin: oran/kapanış saatine bağlı bir yüzeyde birkaç yüz
// milisaniye bile pahalı. Süre dolarsa kupon görüntüsüz gidiyor.
const CAPTURE_TIMEOUT_MS = 2000;

export type CouponImage = string | Promise<string> | undefined;

/**
 * Klonda kuponu KIRPAN kapları açar.
 *
 * Kuponun gövdesi içeriği sığmayınca scroll ediyor (`.coupon-body`: `overflow: auto`,
 * `max-height: 55vh`). Görüntüye yalnız görünen kısım giriyordu; üstelik html2canvas klona
 * scroll konumunu da geri yüklediği için kullanıcı aşağı kaydırmışsa kuponun ÜSTÜ de
 * kesiliyordu.
 *
 * - Kuponun KENDİSİ: boyu koşulsuz serbest (`height: auto`). Böylece uzun kupon içeriği
 *   kadar uzuyor, kısa kupon da altında boş beyaz alan bırakmıyor.
 * - İÇİNDEKİ kaplar (asıl kırpan `.coupon-body` burada): yalnız gerçekten taşıyorsa serbest
 *   bırakılıyor - taşmayan bir kutunun ölçüsüyle oynamanın sebebi yok.
 * - ATALARI: yalnız kırpmaları kaldırılıyor, BOYLARINA DOKUNULMUYOR. Ata boyunu `auto`
 *   yapmak (ör. `height: 100dvh` olan kabuk) kuponun klondaki yerini kaydırırdı.
 *
 * Ölçüm ile yazma ayrı turlarda: mutasyon sırasında `scrollHeight` okumak hem yerleşimi
 * her adımda yeniden hesaplatır hem de kendi değişikliğimizi ölçmeye başlar.
 */
function unclipScrollers(clone: HTMLElement): void {
    const isOverflowing = (node: HTMLElement) =>
        node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1;

    const overflowing = Array.from(clone.querySelectorAll<HTMLElement>("*")).filter(isOverflowing);

    const ancestors: HTMLElement[] = [];
    for (let parent = clone.parentElement; parent; parent = parent.parentElement) {
        ancestors.push(parent);
    }

    [clone, ...overflowing].forEach((node) => {
        node.style.overflow = "visible";
        node.style.maxHeight = "none";
        node.style.height = "auto";
    });
    ancestors.forEach((node) => {
        node.style.overflow = "visible";
        node.style.maxHeight = "none";
    });
}

/**
 * Kupon görüntüsünü ÜRETMEYE BAŞLAR ve promise'ini döner. Promise asla reddetmez;
 * ÜRETİLEMEZSE BOŞ STRING ile çözülür ve kupon yine oynanır - görsel kuponun kendisi değil,
 * yanında giden bir ek. Modal açmadan hemen önce, `await` ETMEDEN çağır.
 */
export function captureCouponImage(): Promise<string> {
    try {
        const element = COUPON_SURFACE_SELECTORS
            .map((selector) => document.querySelector(selector) as HTMLElement | null)
            .find((el) => !!el && el.clientWidth > 0 && el.clientHeight > 0);

        if (!element) {
            return Promise.resolve("");
        }

        const head = element.ownerDocument.head;
        const computed = window.getComputedStyle(element);

        return html2canvas(element, {
            width: element.clientWidth,
            // `height` BİLEREK verilmiyor: html2canvas yüksekliği `onclone`dan SONRA klonun
            // sınırlarından okuyor, yani kırpması açılmış kuponun tam boyu kendiliğinden
            // geliyor. Canlı `clientHeight` verilseydi görüntü yine görünen kutuda kalırdı.
            // 4nala ile aynı: tam çözünürlük gereksiz büyük bir gövde üretiyor.
            scale: 0.7,
            logging: false,
            // Varsayılan 15 sn: yüklenemeyen tek bir görsel tüm çekimi kilitliyordu.
            imageTimeout: 3000,
            ignoreElements: (node: Element) =>
                !(node.contains(element) || element.contains(node) || head.contains(node)),
            // Kuponun GENİŞLİĞİ canlı ölçüsüne sabitleniyor; boy sabitlenmiyor,
            // `unclipScrollers` onu içeriğe bırakıyor.
            onclone: (_doc: Document, clone: HTMLElement) => {
                clone.style.boxSizing = "content-box";
                clone.style.width = computed.width;
                clone.style.minWidth = "0";
                clone.style.maxWidth = "none";
                clone.style.flex = "none";
                unclipScrollers(clone);
            },
        })
            .then((canvas) => canvas.toDataURL("image/jpeg"))
            .catch((error) => {
                console.error("Kupon görüntüsü üretilemedi", error);
                return "";
            });
    } catch (error) {
        console.error("Kupon görüntüsü üretilemedi", error);
        return Promise.resolve("");
    }
}

/** Gönderim anında görüntüyü çözer; süre dolarsa boş string döner, kupon beklemez. */
export function resolveCouponImage(image: CouponImage, timeoutMs: number = CAPTURE_TIMEOUT_MS): Promise<string> {
    if (typeof image === "string") return Promise.resolve(image);
    if (!image) return Promise.resolve("");

    return Promise.race([
        image,
        new Promise<string>((resolve) => setTimeout(() => resolve(""), timeoutMs)),
    ]).catch(() => "");
}
